import React, { useEffect, useRef, useCallback } from "react";
import {
  Map,
  FileDown,
  FileText,
  Play,
  Search,
  PlusIcon,
  Trash,
} from "lucide-react";
import { Bar, Pie } from "react-chartjs-2";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import assessedIcon from "../assets/assessed-icon.svg";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import RightSidebar from "../components/RightSidebar";
import Modal from "../components/Modal";
import useSurveyManagementStore from "../stores/surveyManagementStore";
import debounce from "lodash.debounce";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

ModuleRegistry.registerModules([AllCommunityModule]);

const SurveyManagement = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDetails, setIsDetails] = React.useState(false);
  const [createSurvey, setCreateSurvey] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [processingComplete, setProcessingComplete] = React.useState(false);
  const [isInteractiveMap, setIsInteractiveMap] = React.useState(false);
  const [refreshPage, setRefreshPage] = React.useState(false);
  const [selectPlayActionButton, setSelectPlayActionButton] =
    React.useState(null);
  const [selectMapButton, setSelectMapButton] = React.useState(null);
  const [isReportBtn, setIsReportBtn] = React.useState(false);
  const [selectFileBtn, setSelectFileBtn] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [formData, setFormData] = React.useState({
    roadName: "",
  });
  const [rowData, setRowData] = React.useState([]);

  const {
    surveys,
    isLoading,
    fetchSurveys,
    createRoadSurvey,
    deleteSurvey,
    totalDistanceAll,
    totalDistance,
    overallSeverity,
    overallSeverityAll,
    defectAnalysis,
    defectAnalysisAll,
    downloadReport,
    searchByRoadName, // Updated from searchByName to searchByRoadName
  } = useSurveyManagementStore();

  const { roadName } = formData;

  const fetchSurveysRef = useRef(fetchSurveys);

  // Updated debounced search function to use searchByRoadName
  const debouncedSearch = useCallback(
    debounce(async (value) => {
      if (!value.trim()) {
        await fetchSurveys();
      } else {
        await searchByRoadName(value);
      }
    }, 300),
    [fetchSurveys, searchByRoadName]
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchSurveysRef.current();
  }, []);

  useEffect(() => {
    fetchSurveys();
    totalDistanceAll();
    defectAnalysis();
    overallSeverity();
  }, [refreshPage]);

  useEffect(() => {
    if (
      !isOpen &&
      !isDetails &&
      !isInteractiveMap &&
      !isReportBtn &&
      processingComplete
    ) {
      setRefreshPage((prev) => !prev);
      setProcessingComplete(false);
    }
  }, [isOpen, isDetails, isInteractiveMap, isReportBtn, processingComplete]);

  const arrayDefectAnalysis = Object.entries(
    defectAnalysisAll?.defectAnalysis || {}
  );
  const defectLabels = arrayDefectAnalysis.map(([key]) => key);
  const defectValue = arrayDefectAnalysis.map(([key, value]) => value);

  useEffect(() => {
    const formattedData = surveys.map((survey) => ({
      ...survey,
      critical: survey.severity?.critical,
      high: survey.severity?.high,
      medium: survey.severity?.medium,
      low: survey.severity?.low,
      normal: survey.severity?.normal,
    }));
    setRowData(formattedData);
  }, [surveys]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createRoadSurvey({ roadName });
      setFormData({ roadName: "" });
      setCreateSurvey(false);
      setRefreshPage((prev) => !prev);
    } catch (error) {
      console.error("Error creating survey:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const barChartData = {
    labels: defectLabels,
    datasets: [
      {
        label: ["Critical", "High", "Medium", "Low", "Normal"],
        data: defectValue,
        backgroundColor: [
          "#ff7d74",
          "#ee9c5e",
          "#fbd24e",
          "#b7db40",
          "#19b246",
        ],
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  const barChartOptions = {
    indexAxis: "x",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 25,
          max: 100,
        },
      },
    },
  };

  const calculateOverAllSeverity = {
    totalCritical: surveys.reduce(
      (acc, survey) => acc + survey?.severity?.critical,
      0
    ),
    totalHigh: surveys.reduce((acc, survey) => acc + survey?.severity?.high, 0),
    totalMedium: surveys.reduce(
      (acc, survey) => acc + survey?.severity?.medium,
      0
    ),
    totalLow: surveys.reduce((acc, survey) => acc + survey?.severity?.low, 0),
    totalNormal: surveys.reduce(
      (acc, survey) => acc + survey?.severity?.normal,
      0
    ),
  };

  const handleDelete = async (id) => {
    try {
      await deleteSurvey(id);
      setRefreshPage((prev) => !prev);
      setIsOpen(false);
    } catch (error) {
      console.error("Error deleting survey:", error);
    }
  };

  const pieChartData = {
    labels: ["Critical", "High", "Medium", "Low", "Normal"],
    datasets: [
      {
        data: [
          calculateOverAllSeverity.totalCritical,
          calculateOverAllSeverity.totalHigh,
          calculateOverAllSeverity.totalMedium,
          calculateOverAllSeverity.totalLow,
          calculateOverAllSeverity.totalNormal,
        ],
        backgroundColor: [
          "#ff6b6b",
          "#ff9f43",
          "#feca57",
          "#1dd1a1",
          "#10ac84",
        ],
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          boxHeight: 15,
          display: "flex",
          flexDirection: "column",
          font: {
            size: 12,
            weight: "bold",
            family: "Arial",
            color: "#000000",
            lineHeight: 1.2,
          },
        },
      },
    },
  };

  const ActionsRenderer = (params) => {
    return (
      <div className="flex items-center gap-2">
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => {
            setSelectMapButton(params?.data);
            setSelectedRow(params?.data);
            setIsInteractiveMap(true);
          }}
        >
          <Map size={20} className="text-blue-800" />
        </button>
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => {
            setSelectPlayActionButton(params?.data);
            localStorage.setItem("surveyId", params?.data?._id);
            setIsOpen(true);
          }}
        >
          <Play size={20} className="text-blue-800" />
        </button>
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => {
            setIsReportBtn(true);
            setSelectFileBtn(params?.data);
          }}
        >
          <FileText size={20} className="text-blue-800" />
        </button>
        <button
          className="p-1 hover:bg-gray-100 rounded"
          onClick={() => handleDelete(params?.data?._id)}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="text-white text-xl">Loading...</div>
            </div>
          ) : (
            <Trash size={20} className="text-red-800" />
          )}
        </button>
      </div>
    );
  };

  const RoadNameRenderer = (params) => {
    return (
      <button
        className="text-blue-600 font-bold"
        onClick={() => {
          setSelectedRow(params?.data);
          setIsDetails(true);
        }}
      >
        {params.value}
      </button>
    );
  };

  const [colDefs, setColDefs] = React.useState([
    {
      field: "roadName",
      headerName: "Road Name",
      width: 200,
      headerStyle: { backgroundColor: "#e8eff3" },
      cellRenderer: RoadNameRenderer,
    },
    {
      field: "critical",
      flex: 1,
      headerStyle: { backgroundColor: "#e8eff3" },
      headerName: "Critical (M)",
    },
    {
      field: "high",
      flex: 1,
      headerStyle: { backgroundColor: "#e8eff3" },
      headerName: "High (M)",
    },
    {
      field: "medium",
      flex: 1,
      headerStyle: { backgroundColor: "#e8eff3" },
      headerName: "Medium (M)",
    },
    {
      field: "low",
      flex: 1,
      headerStyle: { backgroundColor: "#e8eff3" },
      headerName: "Low (M)",
    },
    {
      field: "normal",
      flex: 1,
      headerStyle: { backgroundColor: "#e8eff3" },
      headerName: "Normal (M)",
    },
    {
      headerName: "Action",
      flex: 1,
      cellRenderer: ActionsRenderer,
      headerStyle: { backgroundColor: "#e8eff3" },
      sortable: false,
      filter: false,
    },
  ]);

  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  const gridOptions = {
    rowStyle: {
      backgroundColor: "#ffffff",
      border: "none",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
      marginBottom: "10px",
      padding: "10px",
      fontSize: "16px",
    },
    headerHeight: 50,
  };

  return (
    <div className="mt-20 w-full flex flex-col gap-6">
      {/* Loading indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-white text-xl">Loading...</div>
        </div>
      )}

      <div className="w-full pb-4 px-4 shadow-lg flex justify-between items-center">
        {/* Left Section */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-4xl font-bold">
              {totalDistance.totalDistanceAll}
            </h1>
            <img src={assessedIcon} className="p-2 rounded-lg bg-[#dbeaff]" />
          </div>
          <span className="text-lg">Total Distance</span>
          <button
            className="mt-6 bg-[#a9e5f7] px-4 py-2 rounded-md flex items-center gap-2 text-[#074659] font-bold"
            onClick={async () => {
              try {
                await downloadReport();
              } catch (error) {
                alert("Failed to download report. Please try again.");
              }
            }}
            disabled={isLoading}
          >
            <FileDown size={20} />
            {isLoading ? "DOWNLOADING..." : "DOWNLOAD REPORT"}
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <h1 className="font-bold text-lg">OVERALL SEVERITY</h1>
          <div className="w-[300px] h-[200px]">
            <Pie data={pieChartData} options={pieChartOptions} />
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          <h1 className="font-bold text-lg">DEFECT ANALYSIS</h1>
          <div className="w-[500px] h-[200px]">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>
      </div>

      <div className="mt-4 px-6 flex justify-between items-center">
        <button
          className="flex items-center gap-2 bg-[#a9e5f7] text-[#074659] font-bold px-4 py-2 rounded-full"
          onClick={() => setCreateSurvey(true)}
        >
          <PlusIcon size={30} />
          CREATE SURVEY
        </button>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by Road Name"
            className="pl-10 pr-20 py-2 border border-[#bcc9db] placeholder:text-[#6f7987] placeholder:text-sm placeholder:font-semibold rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
        </div>
      </div>

      <div className="pb-3">
        <div
          className="ag-theme-alpine"
          style={{
            height: "350px",
            width: "100%",
            "--ag-row-hover-color": "#afb3b6",
            "--ag-borders": "none",
            "--ag-row-height": "100px",
            "--ag-header-height": "40px",
            "--ag-gap-between-rows": "0px",
            "--ag-borders": "none",
            "--ag-borders-secondary": "none",
            "--ag-cell-horizontal-border": "none",
            "--ag-row-border-color": "transparent",
            "--ag-header-column-separator-display": "none",
            "--ag-header-column-resize-handle-display": "none",
          }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            rowStyle={gridOptions.rowStyle}
            defaultColDef={defaultColDef}
            suppressMovableColumns={true}
            rowHeight={43}
            headerHeight={50}
            pagination={true}
            paginationPageSize={8}
            suppressRowHoverHighlight={false}
          />
        </div>
      </div>

      {(isOpen || isDetails || isInteractiveMap || isReportBtn) && (
        <RightSidebar
          showModalDetails={isDetails}
          setShowModalDetails={setIsDetails}
          showInteractiveMap={isInteractiveMap}
          setShowInteractiveMap={setIsInteractiveMap}
          showReportBtn={isReportBtn}
          setShowReportBtn={setIsReportBtn}
          showModal={isOpen}
          setShowModal={setIsOpen}
          data={selectedRow}
          selectedPlayActionButton={selectPlayActionButton}
          selectMapButton={selectMapButton}
          selectFileBtn={selectFileBtn}
        />
      )}

      <Modal isOpen={createSurvey}>
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="w-[500px]">
              <div className="flex flex-col">
                <h1 className="font-bold text-lg">CREATE SURVEY</h1>
                <hr className="w-full border border-[#bcc9db] mt-2" />
              </div>

              <div className="mt-5 max-w-[400px] flex flex-col">
                <label
                  htmlFor="surveyName"
                  className="font-semibold text-[#949494] text-sm"
                >
                  Road Name
                </label>
                <input
                  type="text"
                  id="surveyName"
                  name="roadName"
                  value={roadName}
                  onChange={handleChange}
                  placeholder="Enter Road Name"
                  className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1"
                  required
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 bg-[#f5fdff] text-[#ffffff] mt-5 px-3 py-3 ">
            <button
              type="button"
              className="bg-[#7e90ab] px-5 py-1 rounded-full"
              onClick={() => setCreateSurvey(false)}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="bg-[#063269] px-5 py-1 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? "CREATING..." : "SUBMIT"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SurveyManagement;
